/**
 * Event Banner - Shows active events like Happy Hour with countdown
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { http } from '../../api/http';

interface ActiveEvent {
  id: string;
  name: string;
  description: string;
  type: string;
  multiplier: number;
  startsAt: string;
  endsAt: string;
}

interface EventsData {
  activeEvents: ActiveEvent[];
  nextHappyHour: { startsAt: string; endsAt: string };
  currentXPMultiplier: number;
  isHappyHourActive: boolean;
}

export const EventBanner: React.FC = () => {
  const [eventsData, setEventsData] = useState<EventsData | null>(null);
  const [countdown, setCountdown] = useState<string>('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await http.get<EventsData>('/events');
        setEventsData(data);
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };

    fetchEvents();
    // Refresh every 5 minutes
    const interval = setInterval(fetchEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!eventsData) return;

    const updateCountdown = () => {
      const now = Date.now();

      if (eventsData.isHappyHourActive && eventsData.activeEvents.length > 0) {
        // Count down to end of happy hour
        const happyHour = eventsData.activeEvents.find((e) => e.id === 'happy_hour');
        if (happyHour) {
          const endTime = new Date(happyHour.endsAt).getTime();
          const remaining = endTime - now;
          if (remaining > 0) {
            const hours = Math.floor(remaining / (1000 * 60 * 60));
            const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
            const secs = Math.floor((remaining % (1000 * 60)) / 1000);
            setCountdown(hours > 0 ? `${hours}h ${mins}m ${secs}s` : `${mins}m ${secs}s`);
          }
        }
      } else {
        // Count down to next happy hour
        const nextStart = new Date(eventsData.nextHappyHour.startsAt).getTime();
        const remaining = nextStart - now;
        if (remaining > 0) {
          const hours = Math.floor(remaining / (1000 * 60 * 60));
          const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
          setCountdown(hours > 0 ? `${hours}h ${mins}m` : `${mins}m`);
        }
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [eventsData]);

  if (!eventsData || dismissed) return null;

  const hasActiveEvents = eventsData.activeEvents.length > 0;

  // Don't show banner if no active events and more than 2 hours until next happy hour
  if (!hasActiveEvents) {
    const nextStart = new Date(eventsData.nextHappyHour.startsAt).getTime();
    const hoursUntil = (nextStart - Date.now()) / (1000 * 60 * 60);
    if (hoursUntil > 2) return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-x-0 top-16 z-40 mt-safe sm:top-14 px-3 py-1.5 text-center text-xs ${
          hasActiveEvents
            ? 'bg-amber-500/10 border-b border-amber-400/20'
            : 'bg-blue-500/5 border-b border-blue-400/10'
        }`}
      >
        {hasActiveEvents ? (
          <div className="flex items-center justify-center gap-2">
            <span className="font-semibold text-amber-300/90">
              {eventsData.activeEvents.map((e) => e.name).join(' + ')}
            </span>
            <span className="text-amber-200/70">{eventsData.currentXPMultiplier}x XP</span>
            <span className="text-amber-200/40">· {countdown} left</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <span className="text-blue-300/70">Happy Hour in {countdown}</span>
            <span className="text-blue-200/40">· 2x XP</span>
          </div>
        )}

        {/* Dismiss button */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 text-xs"
        >
          ✕
        </button>
      </motion.div>
    </AnimatePresence>
  );
};
